/**
 * RenameLayoutModal Component
 *
 * Modal for renaming an existing saved layout.
 * Pre-fills with current name, auto-selects text on focus.
 *
 * Follows StrategyRenameModal pattern: local state, reset on open,
 * validation clears on change, toast on error.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useToastNotification,
} from '@enfomentis/ui';

// ===== TYPES =====

export interface RenameLayoutModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback with validated name on confirm */
  onConfirm: (name: string) => Promise<void>;
  /** Current layout name (pre-filled in input) */
  currentName: string;
}

// ===== COMPONENT =====

export const RenameLayoutModal: React.FC<RenameLayoutModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentName,
}) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToastNotification();

  // Reset form when modal opens or currentName changes
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setNameError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, currentName]);

  const handleNameChange = (value: string): void => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    e.target.select();
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return;
    }

    // No-op if name unchanged
    if (trimmedName === currentName) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(trimmedName);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to rename layout';
      showToast({ color: 'danger', title: 'Error', description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" placement="top-center">
      <ModalContent>
        <ModalHeader>Rename Layout</ModalHeader>
        <ModalBody>
          <Input
            label="Name"
            placeholder="Enter layout name"
            value={name}
            onValueChange={handleNameChange}
            onFocus={handleFocus}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
            Rename
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
